import type { QueryClient } from '@tanstack/react-query';
import { type CacheableScene, optimisticTransform } from '@/data/editorDataLayer/internal/textAtomCacheManager';
import { rollbackQueries } from '@/hooks/optimisticHelpers';

interface CacheUpdateResult {
    previousData: Array<{ queryKey: readonly unknown[]; data: unknown }>;
}

/**
 * Applies an updater function to all cached text atom queries.
 * Handles the common pattern: cancel queries → snapshot → apply transform → return rollback data.
 *
 * The `updater` receives each scene object and should return the modified scene.
 *
 * Delegates to the shared textAtomCacheManager which uses the unified query predicate.
 */
export async function withTextAtomCacheUpdate<T extends CacheableScene = CacheableScene>(
    queryClient: QueryClient,
    updater: (scene: T) => T,
): Promise<CacheUpdateResult> {
    const { snapshots } = await optimisticTransform(queryClient, updater);
    return {
        previousData: snapshots.map(s => ({ queryKey: s.queryKey, data: s.data })),
    };
}

/** Rollback previously captured text atom cache updates */
export function rollbackTextAtomCacheUpdate(
    queryClient: QueryClient,
    previousData: Array<{ queryKey: readonly unknown[]; data: unknown }>,
) {
    rollbackQueries(queryClient, previousData);
}
